import {
  MutableRefObject,
  ReactNode,
  createContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

import styled from 'styled-components'

import { DefaultsType, MapContextType } from './Types/mapboxtypes'
import { Help } from './Components/Help'
import { useEffectOnce, useWindowSize } from 'usehooks-ts'
import smoothscroll from 'smoothscroll-polyfill'

// kick off the polyfill!
smoothscroll.polyfill()

mapboxgl.accessToken =
  'pk.eyJ1IjoidGEtaW50ZXJha3RpdiIsImEiOiJjaXNhNGFsdHAwMDB2Mm9wNWdlMTlqMTNuIn0.uj5aGvF3yvEM4V6kNdlbVg'

const MapboxDefaults = {
  pointerColor: '#fff',
}

function changeDefault(prop: keyof DefaultsType, value: any) {
  MapboxDefaults[prop] = value
}

export const MapContext = createContext<MapContextType>({} as MapContextType)

export const Mapbox = ({
  children,
  colorMode = 'light',
  lang = 'de',
  scrollTarget,
}: {
  children?: ReactNode
  colorMode?: 'dark' | 'light' | undefined
  lang?: 'de' | 'fr' | undefined
  scrollTarget?: HTMLDivElement | null
}) => {
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<mapboxgl.Map | undefined>()
  //
  const [isFullscreen, setFullscreenActive] = useState(false)
  const [terrainActive, setTerrainActive] = useState(false)
  const [isInteractive, setIsInteractive] = useState(false)
  const [helpVisible, setShowHelp] = useState(false)
  const [headerHeight, setHeaderHeight] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const windowSize = useWindowSize()
  const [height, setHeight] = useState<number>(windowSize.height)

  const [viewToReturnTo, setViewToReturnTo] = useState<
    mapboxgl.CameraOptions | undefined
  >()

  useEffect(() => {
    map.current?.resize()
  }, [height])

  useEffect(() => {
    if (window.innerWidth < 712) {
      setIsMobile(true)
    } else {
      setIsMobile(false)
    }
    setHeight(windowSize.height)
  }, [windowSize.width, isFullscreen])

  useEffect(() => {
    setHeight(windowSize.height)
  }, [])

  useEffectOnce(() => {
    const headerElement = document.querySelector('[class*=FullHeader_root]')
    if (headerElement) {
      setHeaderHeight(headerElement?.clientHeight || 0)
    }
  })
  //
  //
  //
  function handleTerrainChange(e: boolean) {
    console.log('___CHANGE TERRAIN')
    setTerrainActive(e)
  }
  function handleFullscreenChange(e: boolean) {
    setFullscreenActive(!isFullscreen)
  }
  function handleHelpChange(e: boolean) {
    setShowHelp(e)
  }
  function handleInteractiveChange(_isInteractive: boolean) {
    if (!_isInteractive) {
      console.log('----- STATIC')
      handleTerrainChange(false)
      setTerrainActive(false)
    } else {
      console.log('----- ACTIVE')

      // setFullscreenActive(true)
    }
    setIsInteractive(_isInteractive)
  }
  function handleViewChange(e: mapboxgl.CameraOptions) {
    setViewToReturnTo(e)
  }

  //
  const [currentContext, setCurrentContext] = useState<MapContextType>({})

  //
  //
  //

  useEffect(() => {
    if (map.current) return
    if (!mapContainer.current) return
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      zoom: 5,
    })
    setCurrentContext({ ...currentContext, map: map.current })
  })
  useEffect(() => {
    setCurrentContext({ ...currentContext, scrollTarget: scrollTarget })
  }, [scrollTarget])

  // useEffect(() => {
  //   if (!mapContainer.current) return
  //   console.log('GET CONTAINER', mapContainer.current)

  //   if (getContainer) getContainer(mapContainer.current)
  // }, [mapContainer.current])

  useEffect(() => {
    if (!map.current) return

    if (!isInteractive) {
      setFullscreenActive(false)
    }
    // if (isMobile && !isInteractive) {
    //   setFullscreenActive(true)
    //   // if (!isFullscreen) setIsInteractive(false)
    // }
    // if (isFullscreen || (isMobile && isInteractive)) {
    //   map.current.resize()
    //   map.current.getCanvasContainer().scrollIntoView({ behavior: 'smooth' })
    // }
  }, [isInteractive])

  useEffect(() => {
    if (!map.current) return

    if (isFullscreen) {
      map.current.resize()
      // map.current.getCanvasContainer().scrollIntoView({ behavior: 'smooth' })
    } else {
      map.current.resize()
      setIsInteractive(false)
    }
  }, [isFullscreen])

  useEffect(() => {
    setCurrentContext({
      map: map.current,
      isMobile,
      //
      zoomDuration: 500,
      colorMode: colorMode,
      scrollTarget: scrollTarget,
      //
      setFullscreen: handleFullscreenChange,
      setTerrain: handleTerrainChange,
      setShowHelp: handleHelpChange,
      setIsInteractive: handleInteractiveChange,
      setViewToReturnTo: handleViewChange,
      //
      viewToReturnTo: viewToReturnTo,
      helpVisible,
      terrainActive,
      isFullscreen,
      isInteractive,
      //
      changeDefault: changeDefault,
    })
  }, [
    isFullscreen,
    helpVisible,
    isInteractive,
    terrainActive,
    viewToReturnTo,
    isMobile,
    map.current,
  ])
  console.log('MAPBOX')

  return (
    <MapWrapper
      style={{
        height: isFullscreen ? windowSize.height + 'px' : undefined,
        maxWidth: '100vw',
        overflow: 'hidden',
      }}
    >
      <FullScreenContainer
        className='fullscreen-container'
        style={{
          zIndex: isFullscreen ? 1002 : undefined,
          position: isFullscreen ? 'fixed' : 'relative',
          top: isFullscreen ? headerHeight : undefined,
          left: isFullscreen ? 0 : undefined,
          bottom: isFullscreen ? 0 : undefined,
          right: isFullscreen ? 0 : undefined,
          height: isFullscreen
            ? windowSize.height - headerHeight + 'px'
            : undefined,
          width: isFullscreen ? '100vw' : undefined,
        }}
      >
        <MapContext.Provider value={currentContext}>
          {height && (
            <MapContainer
              className='map-container'
              pointerColor={MapboxDefaults.pointerColor}
              isInteractive={isInteractive}
              ref={mapContainer}
              minHeight={height * 0.85}
            />
          )}

          {children}
          <Help visible={helpVisible} />
        </MapContext.Provider>
      </FullScreenContainer>
      {/* <HeightDebug>{windowSize.height} | { height }</HeightDebug> */}
      {/* <ContextDebugger context={currentContext} /> */}
    </MapWrapper>
  )
}
const ContextDebugger = ({ context }: { context: MapContextType }) => {
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    >
      <pre
        style={{
          pointerEvents: 'none',
          position: 'absolute',
          top: 0,
          left: 0,
          background: 'rgba(0,0,0,0.5)',
          color: '#fff',
          padding: '10px',
          margin: 0,
        }}
      >
        {Object.entries(context)
          .filter((f) => typeof f[1] !== 'function')
          .map(([key, value]) => {
            if (key === 'map') return null
            return (
              <div>
                {key}: {'' + value}
              </div>
            )
          })}
      </pre>
    </div>
  )
}

const HeightDebug = styled.div`
  position: absolute;
  bottom: 200px;
  left: 50%;
  z-index: 1002;
`
const MapWrapper = styled.div`
  position: relative;
  overflow: hidden;
  margin-bottom: 60px;
`
const FullScreenContainer = styled.div`
  width: 100%;
  height: 100%;
`
const MapContainer = styled.div<{
  pointerColor: string | undefined
  isInteractive: boolean
  minHeight: number
}>`
  position: relative;
  width: 100%;
  min-height: ${(props) => props.minHeight}px;
  height: 100%;
  @media screen and (max-width: 711px) {
    .mapboxgl-ctrl-bottom-left {
      bottom: 0px;
      overflow: visible;
    }
    .mapboxgl-ctrl-bottom-right {
      right: -15px;
      bottom: -3px;

      overflow: visible;
    }
    .mapboxgl-ctrl-attrib-inner {
      padding-top: 5px;
    }
  }

  // ANNOTATION POPUP POINTER
  .mapboxgl-popup-anchor-top .mapboxgl-popup-tip,
  .mapboxgl-popup-anchor-top-left .mapboxgl-popup-tip,
  .mapboxgl-popup-anchor-top-right .mapboxgl-popup-tip {
    border-bottom-color: ${(props) => props.pointerColor};
  }
  .mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip,
  .mapboxgl-popup-anchor-bottom-left .mapboxgl-popup-tip,
  .mapboxgl-popup-anchor-bottom-right .mapboxgl-popup-tip {
    border-top-color: ${(props) => props.pointerColor};
  }
  .mapboxgl-popup-anchor-left .mapboxgl-popup-tip {
    border-right-color: ${(props) => props.pointerColor};
  }
  .mapboxgl-popup-anchor-right .mapboxgl-popup-tip {
    border-left-color: ${(props) => props.pointerColor};
  }
  .mapboxgl-ctrl-top-left,
  .mapboxgl-ctrl-top-right {
    display: ${(props) => (props.isInteractive ? 'block' : 'block')};
  }
  //
  .annotation-placeholder {
    /* pointer-events: none;
    * {
      pointer-events: none;
    } */
  }
  .mapboxgl-popup-content {
    pointer-events: none;
  }
  .mapboxgl-popup-content {
    padding: 0;
  }
  .mapboxgl-ctrl-attrib {
    font-size: 11px;
    line-height: 1.4em;
  }
  .control-button {
    background-color: #fff;
    /* border: 1px solid var(--gray-3) !important; */
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
    border: 0;
    width: 30px;
    height: 30px;
    border-radius: 5px;
    padding: 0;
    margin: 0;
    margin-bottom: 8px;
    text-align: center;
    &:hover {
      cursor: pointer;
      background-color: var(--gray-5);
    }
  }
  .mapboxgl-ctrl-bottom-right {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    flex-direction: column;
    padding-right: 10px;
  }
  .mapboxgl-ctrl-attrib {
    flex: 0 0 100%;
    margin-top: 10px;
    margin-bottom: 10px;
    background-color: transparent;
  }
  .fullscreen-control {
    margin-top: 10px;
  }

  .mapboxgl-ctrl-group {
    float: unset;
  }
`
